// Single source of truth for "what Evessio fields are safe to keep".
//
// Anything not on these lists is dropped during sanitise. Adding a new
// field here is the ONLY place that needs to change when the web portal
// starts consuming something new.
//
// The ORDER of ALLOWED_QUESTION_FIELDS is also the output order: the
// sanitiser sorts each entry's questions[] by index in this list so the
// JSON is readable (hero -> Image 2 + credit -> Image 3 + credit -> ...).
//
// Rule of thumb before adding: if it names a person, address, phone
// number, email, or anything about the entrant as an individual rather
// than the project, DO NOT add it. The web portal doesn't need it and
// keeping it around creates a GDPR liability we're specifically trying
// to avoid.

// Top-level nomination fields we keep.
//
// `customer_firstname`, `customer_lastname`, `customer_email` are the ONLY
// PII we retain — needed to bootstrap entrant accounts during onboarding.
// Evessio attaches them to the nomination row directly; we used to recover
// them from a separate orders file, but that introduced an out-of-sync
// failure mode (orders dump covers entries that aren't in a filtered
// nominations dump) so the orders file was retired in favour of pulling
// straight off the nomination.
export const NOMINATION_TOP_LEVEL: readonly string[] = [
  'uuid',
  'nomination_code',
  'category_name',
  'customer_firstname',
  'customer_lastname',
  'customer_email',
];

// Whitelisted fields off the nested `transaction` object on a nomination.
// `transaction_status` ("Completed" / "Invoiced") is the paid-status signal
// we use to gate image downloads and entrant onboarding. Anything else on
// the transaction (amounts, currency, the transaction_code, the nested
// customer_uuid, etc.) stays out.
export const TRANSACTION_FIELDS: readonly string[] = [
  'transaction_status',
];

const QUESTION_FIELDS: string[] = [
  // Project metadata
  'Project title',
  'Project city',
  'Project country',
  'Client name',
  // 2026: Evessio merged the separate architect/designer fields into single
  // combined fields. The old split names ('Architect Company name' /
  // 'Design Company name', etc.) no longer appear in the dump.
  'Architecture / Design Company name',
  'Architect / Design Company country',
  'Lead architect or designer name (individual architect or designer)',
  'Link to project location on google maps',
  'Project completion date',
  'Project summary 200 - 500 words',

  // Hero (== image 1)
  'Please upload a high-resolution hero image of the entire project',
  'Hero image credit',
];
// Image 2..10 paired with their credits so they appear together in output.
for (let i = 2; i <= 10; i++) {
  QUESTION_FIELDS.push(`Image ${i}`);
  QUESTION_FIELDS.push(`Image ${i} credit`);
}
// A2 judging boards
QUESTION_FIELDS.push('A2 Board #1');
QUESTION_FIELDS.push('A2 Board #2');
// Project video. "Video content" exists in the Evessio schema but is always
// empty in practice — the real content lives in the three link fields (YouTube
// landscape/portrait and a download link for the raw file).
QUESTION_FIELDS.push('Landscape video link');
QUESTION_FIELDS.push('Portrait video link');
QUESTION_FIELDS.push('Link to download video files');
QUESTION_FIELDS.push('Video copyright information');
// Legacy single-field images — kept for tolerance against pre-changeover
// dumps. Appear after the current image fields in the output order.
QUESTION_FIELDS.push('Project images');
QUESTION_FIELDS.push('Image credits');

export const ALLOWED_QUESTION_FIELDS: readonly string[] = QUESTION_FIELDS;

